import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStreamAuth } from '../hooks/useStreamAuth';
import subscriptionService from '../services/SubscriptionService';
import { APP_CONFIG } from '../config/AppConfig';

const DASHBOARD_TABS = [
  { id: 'subscription', label: 'Subscription' },
  { id: 'account', label: 'Account' },
];

// Informational sections folded into the Account tab (formerly the standalone
// Settings page). On the web each renders body copy plus a direct link to the
// BKFC site (the QR-code variant is for the TV / mobile apps).
const INFO_SECTIONS = [
  {
    id: 'about-us',
    label: 'About',
    heading: 'About Us',
    body: 'Learn more about Bare Knuckle Fighting Championship — our story, our athletes, and the sport.',
    cta: 'About BKFC',
    url: 'https://bkfc.com/about',
  },
  {
    id: 'privacy-policy',
    label: 'Privacy',
    heading: 'Privacy Policy',
    body: 'Read how BKFC collects, uses, and protects your personal information.',
    cta: 'View Privacy Policy',
    url: 'https://bkfc.com/privacy-policy',
  },
  {
    id: 'terms-of-use',
    label: 'Terms',
    heading: 'Terms of Use',
    body: 'Review the terms and conditions that govern your use of BKFC.',
    cta: 'View Terms of Use',
    url: 'https://bkfc.com/terms-of-use',
  },
  {
    id: 'connect',
    label: 'Connect',
    heading: 'Connect With Us',
    body: 'Have a question or need a hand? Get in touch with the BKFC team.',
    cta: 'Contact Us',
    url: 'https://bkfc.com/about/contact',
  },
];

const REASON_LABELS = {
  active: 'Active',
  trialing: 'Free trial',
  grace: 'Active (payment retrying)',
  cancelled_in_period: 'Cancelled',
};

const formatDateLabel = value => {
  if (!value || value === 'infinity') {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// Account management lives on bkfc.com/account only (APP_CONFIG.ACCOUNT_URL). Settings shows the
// plan read-only and links there for every change.
const StreamingDashboard = () => {
  const { user } = useStreamAuth();
  const [activeTab, setActiveTab] = useState('subscription');
  const [activeInfoSection, setActiveInfoSection] = useState('about-us');
  const [loading, setLoading] = useState(true);
  // { determined, entitlement, retryAfterMs }
  const [account, setAccount] = useState(null);
  const retryTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    clearTimeout(retryTimer.current);
    const verdict = await subscriptionService.getEntitlement();
    setAccount(verdict);
    // Undetermined: a lock, never "no subscription"; ask again when the server says to.
    if (!verdict.determined) {
      retryTimer.current = setTimeout(load, verdict.retryAfterMs ?? 60000);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    return () => clearTimeout(retryTimer.current);
  }, [load]);

  const entitlement = account?.entitlement;
  const cancelledInPeriod = entitlement?.reason === 'cancelled_in_period';

  const panel = 'bg-secondary/60 rounded-lg p-6 border border-white/10';
  const heading = 'text-white font-display uppercase tracking-wide mb-3';
  const button =
    'inline-flex items-center px-4 py-2 rounded-lg bg-accent text-primary font-display uppercase tracking-wide';

  return (
    <div className="min-h-screen bg-primary flex flex-col">
      <main className="flex-1 px-6 py-10">
        <div className="w-full max-w-4xl mx-auto">
          <h1 className="text-3xl font-display uppercase tracking-wide text-white mb-8 text-center">
            My Account
          </h1>

          <div className="flex items-center justify-center gap-2 mb-8">
            {DASHBOARD_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-md text-sm font-display uppercase tracking-wide transition-colors ${
                  activeTab === tab.id
                    ? 'bg-accent text-primary'
                    : 'bg-secondary/60 text-white border border-white/10 hover:border-white/30'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="bg-secondary/60 rounded-lg p-6 mb-6 border border-white/10">
            {(user?.first_name || user?.last_name) && (
              <>
                <label className="block text-sm text-white/50 font-body mb-1">Name</label>
                <p className="text-white font-body text-lg mb-4">
                  {[user.first_name, user.last_name].filter(Boolean).join(' ')}
                </p>
              </>
            )}
            <label className="block text-sm text-white/50 font-body mb-1">Email</label>
            <p className="text-white font-body text-lg">{user?.email || '—'}</p>
          </div>

          {activeTab === 'subscription' && (
            <div className="space-y-6">
              <div className={panel}>
                <h2 className={heading}>Current Subscription</h2>
                {loading && !account ? (
                  <p className="text-white/60">Loading subscription details...</p>
                ) : !account?.determined ? (
                  <p className="text-white/60">
                    We couldn&apos;t confirm your subscription just now. Retrying.
                  </p>
                ) : entitlement?.state === 'entitled' ? (
                  <div className="space-y-2 text-sm">
                    <p className="text-white">
                      <span className="text-white/60">Plan:</span> {entitlement.offer?.name || '—'}
                    </p>
                    <p className="text-white">
                      <span className="text-white/60">Status:</span>{' '}
                      {cancelledInPeriod
                        ? `Cancelled — access until ${formatDateLabel(entitlement.expires_at)}`
                        : REASON_LABELS[entitlement.reason] || 'Active'}
                    </p>
                    <p className="text-white">
                      <span className="text-white/60">
                        {cancelledInPeriod ? 'Access Until:' : 'Next Billing:'}
                      </span>{' '}
                      {formatDateLabel(entitlement.expires_at)}
                    </p>
                  </div>
                ) : (
                  <p className="text-white/60">
                    No active subscription found.{' '}
                    <Link to="/subscribe" className="text-accent">
                      Subscribe
                    </Link>
                  </p>
                )}
              </div>

              <div className={panel}>
                <h2 className={heading}>Manage Your Account</h2>
                <p className="text-white/70 text-sm mb-4">
                  Change or cancel your plan, update your card, see your payment history, reset your
                  password or delete your account on bkfc.com.
                </p>
                <a
                  href={APP_CONFIG.ACCOUNT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={button}
                >
                  Manage Account on bkfc.com
                </a>
              </div>
            </div>
          )}

          {activeTab === 'account' && (
            <div className="space-y-6">
              {/* Info sections (About / Privacy / Terms / Connect) */}
              <div className="flex flex-wrap items-center justify-center gap-2">
                {INFO_SECTIONS.map(section => (
                  <button
                    key={section.id}
                    onClick={() => setActiveInfoSection(section.id)}
                    className={`px-4 py-2 rounded-md text-sm font-display uppercase tracking-wide transition-colors ${
                      activeInfoSection === section.id
                        ? 'bg-accent text-primary'
                        : 'bg-secondary/60 text-white border border-white/10 hover:border-white/30'
                    }`}
                  >
                    {section.label}
                  </button>
                ))}
              </div>

              {(() => {
                const info =
                  INFO_SECTIONS.find(section => section.id === activeInfoSection) ||
                  INFO_SECTIONS[0];
                return (
                  <div className="bg-secondary/60 rounded-lg p-6 border border-white/10">
                    <h2 className="text-white font-display uppercase tracking-wide mb-3">
                      {info.heading}
                    </h2>
                    <p className="text-white/70 text-sm mb-5 max-w-2xl">{info.body}</p>
                    <a
                      href={info.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent text-primary font-display uppercase tracking-wide text-sm transition-opacity hover:opacity-90"
                    >
                      {info.cta}
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M14 5h5v5m0-5L10 14m-1 7H6a2 2 0 01-2-2V6a2 2 0 012-2h3"
                        />
                      </svg>
                    </a>
                  </div>
                );
              })()}

              <p className="border-t border-white/10 pt-6 text-center font-body text-sm text-white/60">
                To delete your account, sign in at{' '}
                <a
                  href={APP_CONFIG.ACCOUNT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-400 hover:text-red-300 underline"
                >
                  bkfc.com/account
                </a>{' '}
                and choose Delete Account.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default StreamingDashboard;
